import { create } from 'zustand';
import api from '../lib/api';

// Singleton promise to prevent concurrent checkAuth calls
let checkAuthPromise = null;

/** Decode a JWT payload without verification (for reading claims client-side). */
function decodeJwtPayload(token) {
  try {
    const base64 = token.split('.')[1];
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export const useAuthStore = create((set, get) => ({
  user: JSON.parse(localStorage.getItem('user') || 'null'),
  token: localStorage.getItem('token') || null,
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,
  error: null,
  isImpersonating: !!localStorage.getItem('super_admin_token_backup'),
  impersonationTenant: JSON.parse(localStorage.getItem('impersonation_tenant') || 'null'),

  login: async (username, password, tenantId) => {
    set({ isLoading: true, error: null });
    try {
      const payload = { username, password };
      if (tenantId) payload.tenant_id = Number(tenantId);
      const res = await api.post('/auth/login', payload);
      const { user, accessToken } = res.data;

      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(user));

      set({
        user,
        token: accessToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      return res;
    } catch (err) {
      set({
        isLoading: false,
        error: err.message || 'Login failed',
        errorCode: err.data?.code || null,
        isAuthenticated: false,
      });
      throw err;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    set({
      user: null,
      token: null,
      isAuthenticated: false,
      error: null,
    });

    window.location.href = '/login';
  },

  refreshToken: async () => {
    try {
      const res = await api.post('/auth/refresh', {});
      const newToken = res.data?.accessToken || res.accessToken;
      if (!newToken) throw new Error('No token in response');

      localStorage.setItem('token', newToken);
      set({ token: newToken });
      return newToken;
    } catch {
      get().logout();
      return null;
    }
  },

  /**
   * Verify token validity on app load.
   * Uses a singleton promise so concurrent calls (multiple component mounts)
   * share a single /auth/me request instead of firing duplicates.
   */
  checkAuth: async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      set({ isAuthenticated: false, user: null, token: null });
      return;
    }

    // Deduplicate: if already checking, return the existing promise
    if (checkAuthPromise) return checkAuthPromise;

    checkAuthPromise = (async () => {
      try {
        const res = await api.get('/auth/me');
        const user = res.data?.user || res.data;
        localStorage.setItem('user', JSON.stringify(user));
        set({ user, isAuthenticated: true, token });
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        set({ isAuthenticated: false, user: null, token: null });
      } finally {
        checkAuthPromise = null;
      }
    })();

    return checkAuthPromise;
  },

  clearError: () => set({ error: null, errorCode: null }),

  /**
   * Start impersonation: backup super admin token, switch to tenant user token.
   */
  startImpersonation: (token, tenant, user) => {
    // Backup the super admin token so we can restore it later
    const saToken = localStorage.getItem('super_admin_token');
    localStorage.setItem('super_admin_token_backup', saToken);
    localStorage.setItem('impersonation_tenant', JSON.stringify(tenant));

    // Set the tenant user token for the regular api client
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify({
      id: user.id,
      name: user.name_ar,
      username: user.name_ar,
      role: user.role,
    }));

    set({
      token,
      user: { id: user.id, name: user.name_ar, username: user.name_ar, role: user.role },
      isAuthenticated: true,
      isImpersonating: true,
      impersonationTenant: tenant,
    });
  },

  /**
   * End impersonation: restore super admin token and redirect back.
   */
  endImpersonation: () => {
    const saToken = localStorage.getItem('super_admin_token_backup');
    localStorage.removeItem('super_admin_token_backup');
    localStorage.removeItem('impersonation_tenant');
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Restore the super admin token
    if (saToken) {
      localStorage.setItem('super_admin_token', saToken);
    }

    set({
      token: null,
      user: null,
      isAuthenticated: false,
      isImpersonating: false,
      impersonationTenant: null,
    });

    window.location.href = '/super-admin/tenants';
  },
}));
